/**
 * Suhaani Weaves — collection and search filtering.
 *
 * The filter form is a real GET form that works without JavaScript. This file
 * upgrades it: the same query string is fetched through Shopify's Section
 * Rendering API and swapped in, while `history.pushState` keeps the URL
 * shareable and the browser's back and forward buttons correct.
 */
(function () {
  'use strict';

  const U = window.Urbanshop || {};
  const debounce = U.debounce || ((fn) => fn);

  const section = document.querySelector('[data-section-id]');
  if (!section) return;

  const sectionId = section.getAttribute('data-section-id');
  let activeRequest = null;

  function forms() {
    return Array.from(document.querySelectorAll('[data-facet-form]'));
  }

  /**
   * Builds the query string from a form, dropping empty values so the URL
   * stays clean and Shopify does not receive blank filter params.
   */
  function searchParamsFrom(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value === '' || value === null) return;
      params.append(key, value);
    });
    return params.toString();
  }

  function setBusy(state) {
    forms().forEach((form) => {
      if (state) form.setAttribute('aria-busy', 'true');
      else form.removeAttribute('aria-busy');
    });
  }

  async function render(searchParams, updateHistory) {
    const url = window.location.pathname + (searchParams ? '?' + searchParams : '');

    if (activeRequest) activeRequest.abort();
    activeRequest = new AbortController();
    setBusy(true);

    try {
      const response = await fetch(
        window.location.pathname +
          '?' +
          (searchParams ? searchParams + '&' : '') +
          'section_id=' +
          sectionId,
        { signal: activeRequest.signal }
      );
      if (!response.ok) throw new Error('Filter request failed');

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');

      swap(parsed, '[data-facet-results]');
      swapForms(parsed);
      swapText(parsed, '[data-facet-count]');
      swapText(parsed, '[data-facet-count-mobile]');
      swapText(parsed, '[data-facet-apply-count]');

      if (updateHistory !== false) {
        window.history.pushState({ facets: searchParams }, '', url);
      }

      /**
       * Read the sort back from the response rather than from a value captured
       * at load: Shopify marks the option it actually applied, which is the
       * only reliable source once the URL has no explicit sort_by.
       */
      const serverSort =
        (parsed.querySelector('select[name="sort_by"] option[selected]') || {}).value ||
        (parsed.querySelector('select[name="sort_by"]') || {}).value;
      syncSortControls(serverSort || new URLSearchParams(searchParams).get('sort_by'));

      announceResults();
      const results = document.querySelector('[data-facet-results]');
      if (results) results.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Fall back to a normal navigation rather than leaving a stale grid. */
      window.location.href = url;
    } finally {
      setBusy(false);
      activeRequest = null;
    }
  }

  function swap(parsed, selector) {
    const next = parsed.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.innerHTML = next.innerHTML;
  }

  function swapText(parsed, selector) {
    const next = parsed.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.textContent = next.textContent;
  }

  /**
   * Replaces the filter groups in place while leaving the <form> element
   * itself alone, so an open <details> keeps its state where it can and focus
   * is not thrown away mid-interaction.
   */
  function swapForms(parsed) {
    forms().forEach((form) => {
      const context = form.getAttribute('data-context');
      const next = parsed.querySelector('[data-facet-form][data-context="' + context + '"]');
      if (!next) return;

      const activeId = document.activeElement ? document.activeElement.id : null;

      ['.facets__groups', '.facets__active'].forEach((selector) => {
        const nextPart = next.querySelector(selector);
        const currentPart = form.querySelector(selector);
        if (nextPart && currentPart) {
          currentPart.innerHTML = nextPart.innerHTML;
        } else if (nextPart && !currentPart) {
          form.insertBefore(nextPart.cloneNode(true), form.querySelector('.facets__groups'));
        } else if (!nextPart && currentPart) {
          currentPart.remove();
        }
      });

      if (activeId) {
        const restored = document.getElementById(activeId);
        if (restored) restored.focus({ preventScroll: true });
      }
    });
  }

  /** Keeps every sort control in step, including after a history change. */
  function syncSortControls(value) {
    document
      .querySelectorAll('select[name="sort_by"]')
      .forEach((select) => {
        if (value && select.value !== value) select.value = value;
      });
  }

  function announceResults() {
    const count = document.querySelector('[data-facet-count-mobile]');
    if (count && U.announce) U.announce(count.textContent.trim());
  }

  /** Keeps every filter form in sync, so the drawer and the rail never disagree. */
  function syncOtherForms(source) {
    const params = new URLSearchParams(searchParamsFrom(source));
    forms().forEach((form) => {
      if (form === source) return;
      form.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        const value = params.get('sort_by');
        if (value) select.value = value;
      });
    });
  }

  /* ---- Events ---- */

  /**
   * The toolbar sort sits outside the filter forms, because the filter rail
   * hides its own bar on desktop and the drawer is closed there — leaving no
   * reachable sort control. It merges into whatever filters are already
   * applied instead of replacing them.
   */
  document.addEventListener('change', (event) => {
    const standalone = event.target.closest('[data-facet-sort-standalone]');
    if (!standalone) return;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', standalone.value);
    syncSortControls(standalone.value);
    render(params.toString());
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facet-form]');
    if (!form) return;
    if (event.target.type === 'number') return; /* price inputs are debounced below */
    syncOtherForms(form);
    render(withToolbarSort(searchParamsFrom(form)));
  });

  /* A filter form submission must not lose a sort chosen in the toolbar. */
  function withToolbarSort(params) {
    const toolbar = document.querySelector('[data-facet-sort-standalone]');
    if (!toolbar) return params;
    const p = new URLSearchParams(params);
    if (!p.get('sort_by') && toolbar.value) p.set('sort_by', toolbar.value);
    return p.toString();
  }

  document.addEventListener(
    'input',
    debounce((event) => {
      const form = event.target.closest('[data-facet-form]');
      if (!form || event.target.type !== 'number') return;
      render(searchParamsFrom(form));
    }, 600)
  );

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facet-form]');
    if (!form) return;
    event.preventDefault();
    render(searchParamsFrom(form));
  });

  /* Filter chips, "clear all" and the drawer's clear button. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-link]');
    if (!link) return;
    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    render(url.searchParams.toString());
  });

  /* Pagination stays a real navigation so each page has its own URL. */

  window.addEventListener('popstate', () => {
    render(new URL(window.location.href).searchParams.toString(), false);
  });

  /* The apply button in the drawer just closes it — results are already live. */
})();
